import { IPageConfiguration } from '../../../domain/conf/page'
import { Utils } from '../../../utils/common'
import { DOMUtils } from '../../../utils/dom'
import { IKlangooSensitive } from '../../common/nlp/klangoo-config'
import { IKlangooNegativeKeywords } from '../../common/nlp/klangoo-config'
import { PageType } from '../../common/page-types'
import { AdTargetLookup } from './lookup'
import { IAdTargetStrategyHandler } from './strategy'
import { AdTarget, IAdTargets } from './target'

interface IWindow {
    I10C: any
}

declare let window: IWindow

/**
 * Targeting keys as defined by Ad Ops. Please be vigilant in minimizing additions (be aware of redundancy etc.)
 * DO NOT repeat these key definitions elsewhere (extract if multiple targeters are required).
 */
export enum AdTargetKeys {
    ARTICLE = 'aid',
    ARTICLE_SOURCE = 'asrc',
    AUTHOR = 'author',
    CHANNEL = 'ck',
    COMMUNITY = 'comm',
    ENTITY = 'et',
    IMPRESSION = 'imp',
    LOCATION = 'loc',
    PAGE = 'page',
    PARTNER = 'prtn',
    OPT_OUT_POL = 'no_pol',
    PROPERTY = 'pr',
    REFERRER = 'r',
    REFRESH = 'rc',
    SENSITIVE = 'sensitive',
    SENSITIVE_BRAND = 'scb',
    SENSITIVE_TOPIC = 'sct',
    NEGATIVE = 'negative',
    NEGATIVE_BRAND = 'nkb',
    SHIELD = 'shield',
    SUBCHANNEL = 'sck',
    TAG = 'tag',
    TEST = 'test', // NOTE: should be `yes` unless prod!
    TOPICS = 'topics',
    TYPE = 'type',
    AD_TEST = 'adtest',
    BODY_TYPE = 'bt',
    VEHICLE_TYPE = 'vt',
    SPECIAL_SECTION = 'sect',
    MODEL_YEAR = 'year',
    CAR_MAKE = 'make',
    CAR_MODEL = 'model'
}

export interface IPageTargetingConfig {
    property?: string
    ancestry?: string[]
    articleId?: string
    writers?: IPageConfiguration['writers']
    entity?: string
    optOutPoliticalAds?: boolean
    originId?: string
    wpLegacyId?: string
    refreshCount?: number
    sensitive?: IKlangooSensitive
    negative?: IKlangooNegativeKeywords[]
    topics?: string
    type?: PageType
    bodyType?: string[]
    vehicleType?: string[]
    specialSection?: string[][]
    modelYears?: string[]
    carMake?: string
    carModel?: string
}

export class DefaultAdTargetingStrategy implements IAdTargetStrategyHandler {
    public default = {
        loc: 'top'
    }

    protected lookup: AdTargetLookup
    protected pageTargets: AdTarget[] = []
    protected firstConfig: IPageTargetingConfig

    constructor(lookup: any) {
        this.lookup = new AdTargetLookup(lookup)
    }

    /**
     * Determines ad-level targeting. Typical use is setting `loc` when multiple of same size.
     * Additional targeting is rarely used, but supported.
     *
     * @param el Ad element
     */
    public ad(custom: IAdTargets, loc?: string): AdTarget[] {
        const targets: AdTarget[] = []

        // set default location
        if (!(custom && custom[AdTargetKeys.LOCATION])) {
            targets.push(new AdTarget(AdTargetKeys.LOCATION, loc || this.default.loc))
        }

        Object.entries(custom || {}).forEach((_) => targets.push(new AdTarget(_[0], _[1])))

        return targets
    }

    /**
     * Determines page-level targeting.
     */
    public page(conf: IPageTargetingConfig, iasScores: object = {}): AdTarget[] {
        this.pageTargets = []
        if (!this.firstConfig) {
            this.firstConfig = conf
        }
        // targets using config data
        const articleId = conf.articleId || this.firstConfig.articleId
        this.targetArticle(articleId,
                           conf.originId || this.firstConfig.originId,
                           conf.wpLegacyId || this.firstConfig.wpLegacyId)
        this.targetWriters(conf.writers || this.firstConfig.writers)
        this.targetOptOut(conf.optOutPoliticalAds || this.firstConfig.optOutPoliticalAds)
        this.targetPageType(conf.type || this.firstConfig.type)
        this.targetProperty(conf.property || this.firstConfig.property)
        this.targetSensitive(conf.sensitive || this.firstConfig.sensitive)
        this.targetTopics(conf.topics || this.firstConfig.topics)
        this.targetNegative(conf.negative || this.firstConfig.negative)
        this.targetBodyType(conf.bodyType || this.firstConfig.bodyType)
        this.targetVehicleType(conf.vehicleType || this.firstConfig.vehicleType)
        this.targetSpecialSection(conf.specialSection || this.firstConfig.specialSection)
        this.targetModelYears(conf.modelYears || this.firstConfig.modelYears)
        this.targetCarMake(conf.carMake || this.firstConfig.carMake)
        this.targetCarModel(conf.carModel || this.firstConfig.carModel)

        // targets using env/lookup data
        this.targetArticleSource(articleId)
        this.targetCommunity()
        this.targetEntity()
        this.targetPartner()
        this.targetReferrer()
        this.targetShield()

        // targets using logic based on ancestry and type
        this.targetWithAncestry(conf.ancestry || this.firstConfig.ancestry, conf.type || this.firstConfig.type)

        // `test` - should be 'yes' unless prod!
        // THIS NEEDS TO BE REMOVED LATER
        if (__NON_PROD) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.TEST, 'yes'))
        }

        if(Object.keys(iasScores).length) {
            this.targetWithIasScores(iasScores)
        }

        this.targetAdTest()

        return this.pageTargets
    }

    /**
     * Reset Page Targets Loaded
     */
    public resetPageTargets() {
        this.pageTargets = []
        delete this.firstConfig
    }

    /**
     * Article - from page conf; array of article and origin ids.
     */
    protected targetArticle(articleId: string, originId: string, legacyDrivingGeneratedWPId: string) {
        if (articleId) {
            const aids = [articleId, originId]
            if (legacyDrivingGeneratedWPId) {
                aids.push(legacyDrivingGeneratedWPId)
            }
            this.pageTargets.push(new AdTarget(AdTargetKeys.ARTICLE, aids))
        }
    }

    /**
     * Article Source - lookup by shortcode from canonical url.
     */
    protected targetArticleSource(articleId: string) {
        const canonicalUrl = document.querySelector("link[rel='canonical']") ? DOMUtils.getRelUrl('canonical') : ''
        const articleSource = this.lookup.byShortcode(canonicalUrl)

        if (canonicalUrl !== '' && articleSource && articleId) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ARTICLE_SOURCE, articleSource))
        }
    }

    /**
     * Authors - from page conf; array of author display names.
     */
    protected targetWriters(writers: IPageConfiguration['writers']) {
        if (writers && writers.main && writers.main.length) {
            this.pageTargets.push(new AdTarget(
                AdTargetKeys.AUTHOR, writers.main.map((_) => typeof _ === 'object' ? _.name : _)))
        }
    }

    /**
     * Community - lookup by host.
     * @todo: would be nice if this could accept `1` rather than `y`
     */
    protected targetCommunity() {
        const comm = this.lookup.byHost(location.host, AdTargetKeys.COMMUNITY)
        if (comm) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.COMMUNITY, comm))
        }
    }

    /**
     * Entity - lookup by host; e.g. `sm` (Sun Media).
     */
    protected targetEntity() {
        const entity = this.lookup.byHost(location.host, AdTargetKeys.ENTITY)
        if (entity) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.ENTITY, entity))
        }
    }

    /**
     * Page - set `page` arg to either 'story' or 'index' the same way done for AdURI
     */
    protected targetPageType(type: string) {
        this.pageTargets.push(new AdTarget(
            AdTargetKeys.PAGE,
            type === PageType.STORY ? PageType.STORY : PageType.INDEX
        ))
    }

    /**
     * BodyType - add list concatenated together with commas
     */
    protected targetBodyType(bodyType: string[]) {
        if (bodyType) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.BODY_TYPE, bodyType.join(',')))
        }
    }

    /**
     * VehicleType - add list concatenated together with commas
     */
    protected targetVehicleType(vehicleType: string[]) {
        if (vehicleType) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.VEHICLE_TYPE, vehicleType.join(',')))
        }
    }

    /**
     * SpecialSection - flatten list and then concatenate slugs together with commas
     */
    protected targetSpecialSection(specialSection: string[][]) {
        if (specialSection) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.SPECIAL_SECTION, specialSection.map(
                (sectList: string[]) => sectList.join(','))))
        }
    }

    /**
     * ModelYears - add list concatenated together with commas
     */
    protected targetModelYears(modelYears: string[]) {
        if (modelYears) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.MODEL_YEAR, modelYears.join(',')))
        }
    }

    /**
     * CarMake - add car make if set
     */
    protected targetCarMake(carMake: string) {
        if (carMake) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.CAR_MAKE, carMake))
        }
    }

    /**
     * CarModel - add car model if set
     */
    protected targetCarModel(carModel: string) {
        if (carModel) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.CAR_MODEL, carModel))
        }
    }

    /**
     * Property - from page conf; e.g. `np`.
     * @todo: add to lookup
     * @todo: SHOULD BE DOMAIN NOT LOOKUP
     */
    protected targetProperty(property: string) {
        if (property) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PROPERTY, property))
        }
    }

    /**
     * Partner - lookup by host; e.g. `adperfect`.
     */
    protected targetPartner() {
        const partner = this.lookup.byHost(location.host, AdTargetKeys.PARTNER)
        if (partner) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.PARTNER, partner))
        }
    }

    /**
     * Referrer - fetched from `document`.
     */
    protected targetReferrer() {
        if (document.referrer) {
            const domain = document.referrer.replace(/^.*\/\/([^\/]+).*$/, '$1')
            this.pageTargets.push(new AdTarget(AdTargetKeys.REFERRER, domain))
        }
    }

    /**
     * Refresh count.
     */
    protected targetRefresh(count: number) {
        const counter = (count === undefined) ? 0 : count
        this.pageTargets.push(new AdTarget(AdTargetKeys.REFRESH, String(counter)))
    }

    /**
     * Sensitive content.
     */
    protected targetSensitive(sensitive: IKlangooSensitive) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.SENSITIVE, sensitive ? 'y' : 'n'))

        if (sensitive) {
            if (sensitive.brands && sensitive.brands.length) {
                this.pageTargets.push(new AdTarget(
                    AdTargetKeys.SENSITIVE_BRAND,
                    sensitive.brands.map(({ token }) => token.toLowerCase())))
            }

            if (sensitive.categories && sensitive.categories.length) {
                this.pageTargets.push(new AdTarget(
                    AdTargetKeys.SENSITIVE_TOPIC,
                    sensitive.categories.map(({ name }) => name.toLowerCase())))
            }
        }
    }

    /**
     * Negative Keywords.
     */
    protected targetNegative(negatives: IKlangooNegativeKeywords[]) {
        this.pageTargets.push(new AdTarget(AdTargetKeys.NEGATIVE, negatives && negatives.length > 0 ? 'y' : 'n'))

        if (negatives) {
            const brand: string[] = negatives.map((negative) => {
                if (negative.brand && negative.brand.length) {
                    return negative.brand
                }
                return ''
            })
            if (brand && brand.length) {
                this.pageTargets.push(new AdTarget(
                    AdTargetKeys.NEGATIVE_BRAND,
                    brand))
            }
        }
    }

    /**
     * Target topics (primary and secondary categories set by editorial)
     */
    protected targetTopics(topics: string) {
        if (topics) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.TOPICS, topics))
        }
    }

    /**
     * Shield - for 'Index Exchange' (?)
     * @todo: explain
     */
    protected targetShield() {
        if (window.I10C && window.I10C.Morph) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.SHIELD, `1`))
        }
    }

    /**
     * Handles targeting involving ancestry and type:
     * Channel - usually top-most ancestor; e.g. `news`.
     * Sub-channel - array of ancestor slugs.
     * Impression - usually last-most ancestor; `index` on home pages
     */
    protected targetWithAncestry(ancestry: string[], type: PageType) {

        // has heirarchy (typically categories)
        if (ancestry && ancestry.length) {

            // channel key is top-most ancestor
            this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, ancestry[0]))

            // remaining ancestors are sub-category keys
            if (ancestry.length > 1) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.SUBCHANNEL, ancestry.slice(1)))
            }

            // no impression on story pages
            if (type !== PageType.STORY) {
                // impression is last-most ancestor
                this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, [...ancestry].pop()))
            }

        } else {

            // home page has 'index' as ck and imp
            if (type === PageType.INDEX) {
                this.pageTargets.push(new AdTarget(AdTargetKeys.CHANNEL, PageType.INDEX))
                this.pageTargets.push(new AdTarget(AdTargetKeys.IMPRESSION, PageType.INDEX))
            }
        }
    }

    /**
     * OptOut - currently just for political ads
     */
    protected targetOptOut(optOutPol: boolean) {
        if (optOutPol) {
            this.pageTargets.push(new AdTarget(AdTargetKeys.OPT_OUT_POL, 'true'))
        }
    }

    /**
     * Handles targeting with adtest query string param
     */
    protected targetAdTest() {
        const query: any = Utils.params()
        if ('adtest' in query) {
            const { adtest } = query
            this.pageTargets.push(new AdTarget(AdTargetKeys.AD_TEST, adtest))
        }
    }

    /**
     * Add the tags from IAS (Integral Data Science) to the targeting keys.
     * @param iasScores
     */
    private targetWithIasScores(iasScores: any) {
        for (const key of Object.keys(iasScores)) {
            this.pageTargets.push(new AdTarget(key, iasScores[key] ))
        }
    }
}
